import React, { useState } from 'react';
import { NextUI } from '@/components/NextUI';
import Button from '@/components/NextUI/Button';
import { useAppDispatch } from '@/hooks/redux';
import { showToast } from '@/store/slices/toastActions';
import useAuth from '@/hooks/useAuth';
import { z } from 'zod';
import { authService } from '../../services/authService';

const createEmailSchema = (currentEmail: string | undefined) => {
  const baseSchema = z.object({
    email: z
      .string()
      .min(1, { message: 'Email cannot be empty' })
      .email({ message: 'Please enter a valid email address' })
      .refine((email) => email !== currentEmail, {
        message: 'Email address is the same as the current one',
      }),
  });

  return baseSchema;
};

const EditEmailModal = ({ hideModal }: { hideModal: (data?: any) => void }) => {
  const dispatch = useAppDispatch();
  const { user, fbUser } = useAuth();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const isSocialLogin = fbUser?.providerData.some(
    (provider) =>
      provider.providerId === 'google.com' ||
      provider.providerId === 'facebook.com'
  );

  const emailSchema = createEmailSchema(user?.email);

  const handleUpdateProfile = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    if (!fbUser?.emailVerified) {
      dispatch(
        showToast({
          text: 'Your email address is not verified. Please verify your email address first.',
          variant: 'danger',
        })
      );
      setIsLoading(false);
      hideModal();
      return;
    }

    const payload = new FormData(e.currentTarget);
    const formData = {
      email: payload.get('email') as string,
    };

    const validationData = { email: formData.email };

    const validationResult = emailSchema.safeParse(validationData);

    if (!validationResult.success) {
      const formErrors: { [key: string]: string } = {};
      validationResult.error.errors.forEach((error) => {
        formErrors[error.path[0]] = error.message;
      });
      setErrors(formErrors);
      setIsLoading(false);
      return;
    }

    try {
      await authService.requestEmailChange(formData.email);
      dispatch(
        showToast({
          text: 'Email change request sent. Please verify your new email address.',
          variant: 'info',
        })
      );
    } catch (err: any) {
      dispatch(
        showToast({
          text: err as string,
          variant: 'danger',
        })
      );
      console.error('Error updating profile:', err);
    } finally {
      setIsLoading(false);
      hideModal();
    }
  };

  return (
    <NextUI.Modal
      className="max-w-[500px]"
      classNames={{ content: 'py-8 !px-4 md:!px-8' }}
      defaultOpen={true}
      name={'EditEmailModal'}
    >
      <h2 className="h5 font-semibold text-start text-dark">
        Change Your Email
      </h2>

      <form onSubmit={handleUpdateProfile} className="mt-4" noValidate>
        <div className="space-y-5">
          <NextUI.Input
            type="email"
            name="email"
            formNoValidate
            variant="outline"
            radius="sm"
            placeholder="Enter your email"
            className="mt-5"
            defaultValue={user?.email}
            isInvalid={!!errors.email}
            errorMessage={errors.email}
          />
        </div>
        <div className="flex justify-end gap-4 mt-8">
          <Button
            variant="outline"
            className="w-[160px]"
            onClick={() => {
              hideModal();
            }}
            type="button"
          >
            Cancel
          </Button>
          <Button className="w-[160px]" type="submit" isLoading={isLoading}>
            {isLoading ? '' : 'Change'}
          </Button>
        </div>
      </form>
    </NextUI.Modal>
  );
};

export default EditEmailModal;
